import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MenuLanguageCode } from '../models/menu-language';
import { MenuLanguageService } from './menu-language.service';

import es from '../../assets/i18n/es.json';
import en from '../../assets/i18n/en.json';
import pt from '../../assets/i18n/pt.json';
import fr from '../../assets/i18n/fr.json';
import it from '../../assets/i18n/it.json';
import zh from '../../assets/i18n/zh.json';
import ar from '../../assets/i18n/ar.json';
import de from '../../assets/i18n/de.json';

export type TranslationParams = Record<string, string | number | boolean | null | undefined>;
type TranslationValue = string | { one?: string; other?: string } | TranslationDictionary;
interface TranslationDictionary { [key: string]: TranslationValue; }

const DICTIONARIES: Record<MenuLanguageCode, TranslationDictionary> = { es, en, pt, fr, it, zh, ar, de };
const LOCALES: Record<MenuLanguageCode, string> = {
  es: 'es-CL', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT', zh: 'zh-CN', ar: 'ar', de: 'de-DE'
};

@Injectable({ providedIn: 'root' })
export class UiTranslationService {
  constructor(
    private readonly languages: MenuLanguageService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.applyDocumentLanguage(this.languages.currentLanguage);
    this.languages.language$.subscribe(language => this.applyDocumentLanguage(language));
  }

  get language(): MenuLanguageCode { return this.languages.currentLanguage; }
  get locale(): string { return LOCALES[this.language]; }
  get direction(): 'ltr' | 'rtl' { return this.language === 'ar' ? 'rtl' : 'ltr'; }

  translate(key: string, params: TranslationParams = {}): string {
    const selected = this.lookup(DICTIONARIES[this.language], key);
    const fallback = this.lookup(DICTIONARIES.es, key);
    const value = selected ?? fallback;
    if (value === undefined || (typeof value !== 'string' && !this.isPlural(value))) return '';

    const template = this.isPlural(value)
      ? ((Number(params['count']) === 1 ? value.one : value.other) || value.other || value.one || '')
      : value;
    return template.replace(/\{\{\s*([\w]+)\s*\}\}/g, (_match, name: string) => {
      const replacement = params[name];
      return replacement === null || replacement === undefined ? '' : String(replacement);
    });
  }

  has(key: string, language: MenuLanguageCode = this.language): boolean {
    return this.lookup(DICTIONARIES[language], key) !== undefined;
  }

  private lookup(dictionary: TranslationDictionary, key: string): TranslationValue | undefined {
    return key.split('.').reduce<TranslationValue | undefined>((current, segment) => {
      if (!current || typeof current === 'string' || this.isPlural(current)) return undefined;
      return current[segment];
    }, dictionary);
  }

  private isPlural(value: TranslationValue): value is { one?: string; other?: string } {
    return typeof value === 'object' && value !== null && ('one' in value || 'other' in value);
  }

  private applyDocumentLanguage(language: MenuLanguageCode): void {
    const root = this.document.documentElement;
    root.lang = language;
    root.dir = language === 'ar' ? 'rtl' : 'ltr';
  }
}
